import Server from "./server";
import {JsonDate} from "./misc/types";
import {paramsToQueryString} from "./misc/utils";
import {Order} from "./bo/order";

const server = Server.instance();

export type ListenerReaction = {
    cancelListener: boolean
};

export type Listener<T> = (data: T) => ListenerReaction;

export type GetOrdersOptions = {
    noPast?: boolean,
    silent?: boolean,
    deliveryDate?: JsonDate | JsonDate[],
    deliveryDateSince?: JsonDate,
    deliveryDateNotNull?: boolean
};

const listeners : { [url: string] : Array<Listener<Order[]>> } = {};

let maxAudit = -1;

interface Resource<T> {
    maxAudit: number;
    data: T;
}

const cache: { [url: string]: Resource<Order[]> } = {};

server.addCacheClearListener(() => {
    maxAudit = -1;
    for (const url of Object.keys(cache)) {
        delete cache[url];
    }
    sync();
});

// ------------------------
// Public interface

export interface Subscription {
    topic: string;
    listener: Listener<Order[]>;
}

export function subscribe(opts: GetOrdersOptions, l: Listener<Order[]>, subscription?: Subscription): Subscription {
    const url = toUrlParams(opts);

    if (subscription && subscription.topic === url) {
        // No need to resubscribe
        return subscription;
    }

    // Cancel original subscription
    if (subscription) {
        cancelSubscription(subscription);
    }

    if (! listeners[url]) {
        listeners[url] = [];
    }

    const r = trigger(url, l);
    if (! r.cancelListener) {
        listeners[url].push(l);
    }

    sync();

    return {
        topic: url,
        listener: l
    };
}

function cancelSubscription(s: Subscription) {
    const topic = s.topic;
    if (! listeners[topic]) {
        return;
    }
    listeners[topic] = listeners[topic].filter(l => l !== s.listener);
}

export function getOrders(opts: GetOrdersOptions) : Promise<Order[]> {
    return new Promise<Order[]>((resolve, reject) => {
        const l = (data: Order[]) => {
            resolve(data);
            return {
                cancelListener: true
            };
        };
        subscribe(opts, l);
    });
}

// ------------------------

let syncTimeout: any = undefined;

const REFRESH_MS = 60 * 1000;

function configSync() {
    if (syncTimeout) {
        return;
    }
    const active = Object.keys(listeners).filter(url => listeners[url].length > 0);
    if (active.length > 0) {
        syncTimeout = setTimeout(sync0, REFRESH_MS);
    }
}

export function sync() {
    if (syncTimeout) {
        clearTimeout(syncTimeout);
        syncTimeout = undefined;
    }
    syncTimeout = setTimeout(sync0, 0);
}

async function sync0() {
    if (syncTimeout) {
        clearTimeout(syncTimeout);
        syncTimeout = undefined;
    }
    const a = await await server.syncOrders(true);
    maxAudit = a;
    clearCache();
    configSync();
}

function clearCache() {
    const toRemove : string[] = [];
    for (const url in cache) {
        const r = cache[url];
        if (r.maxAudit !== maxAudit) {
            toRemove.push(url);
        }
    }
    for (const url of toRemove) {
        delete cache[url];
    }
    load();
}

async function load() {
    for (const url in listeners) {
        const already = cache[url];
        if (already) {
            continue;
        }
        loadUrl(url);
    }
}

async function loadUrl(url: string) {
    if (listeners[url].length == 0) {
        return; // No one listening, no need to query
    }
    const ls = [ ...listeners[url] ];

    const orders = await server.getOrdersByQueryString(url);
    cache[url] = {
        maxAudit: maxAudit,
        data: orders
    };
    for (const l of ls) {
        const r = trigger(url, l);
        if (r.cancelListener) {
            listeners[url] = listeners[url].filter(x => x != l);
        }
    }
}

function trigger(url: string, l: Listener<Order[]>) {
    const data = cache[url] && cache[url].data;
    if (data) {
        const r = l(data);
        return r;
    } else {
        return {
            cancelListener: false
        };
    }
}

function toUrlParams(opts: GetOrdersOptions) {
    const params : any = {};
    if (opts.noPast) {
        params["noPast"] = "true";
    }
    if (opts.deliveryDate) {
        params["deliveryDate"] = opts.deliveryDate;
    }
    if (opts.deliveryDateSince) {
        params["deliveryDateSince"] = opts.deliveryDateSince;
    }
    if (opts.deliveryDateNotNull) {
        params["deliveryDateNotNull"] = "true";
    }
    const query = paramsToQueryString(params);

    return query;
}




